import React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { fmtDays, fmtHours } from './calendarFormat';

// Lejant (spec §3.4): her çip bir katmanı açar/kapatır (aria-pressed); üzerine gelince o katman öne çıkar.
// Dar ekranda kısa adlar, sayılar gizli, tek satır. LM kullanılamıyorsa yalnız "holiday" katmanı gelir.
export default function CalendarLegend({ layers, visible, totals, onToggle, onFocusLayer }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const countText = (k) => {
    if (k === 'holiday') return fmtDays(t, lang, totals.holidayDays);
    const hours = totals[`${k}Hours`];
    const parts = [fmtDays(t, lang, totals[`${k}Days`])];
    if (hours > 0) parts.push(fmtHours(t, lang, hours));
    return parts.join(' · ');
  };

  return (
    <Legend role="group" aria-label={t('myCalendar.title')}>
      {layers.map((k) => {
        const on = visible[k];
        return (
          <Chip
            key={k}
            type="button"
            data-k={k}
            data-testid={`legend-chip-${k}`}
            aria-pressed={on ? 'true' : 'false'}
            $on={on}
            $kind={k}
            onClick={() => onToggle(k)}
            onMouseEnter={() => on && onFocusLayer(k)}
            onMouseLeave={() => onFocusLayer(null)}
            onFocus={() => on && onFocusLayer(k)}
            onBlur={() => onFocusLayer(null)}
          >
            <span className="sw" />
            <span className="lbl">
              <span className="long">{t(`myCalendar.legend.${k}`)}</span>
              <span className="short">{t(`myCalendar.legendShort.${k}`)}</span>
            </span>
            <span className="cnt">{countText(k)}</span>
          </Chip>
        );
      })}
    </Legend>
  );
}

const Legend = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 6px;
    flex-wrap: nowrap;
  }
`;

const swatch = {
  holiday: (c) => css`background: ${c.holidayBg}; box-shadow: inset 0 0 0 1.5px ${c.holidayText};`,
  approved: (c) => css`background: ${c.approved};`,
  waiting: (c) => css`background: ${c.waitingBg}; border: 1.5px dashed ${c.waitingBorder};`,
};

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  background: ${({ theme }) => theme.colors.surface.card};
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  user-select: none;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.calendar.chipHoverBorder};
    background: ${({ theme }) => theme.colors.calendar.chipHoverBg};
  }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary.main}; outline-offset: 2px; }

  .sw { width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0; box-sizing: border-box; ${({ theme, $kind }) => swatch[$kind](theme.colors.calendar)} }
  .cnt { color: ${({ theme }) => theme.colors.text.muted}; font-variant-numeric: tabular-nums; }
  .short { display: none; }

  ${({ $on, theme }) => !$on && css`
    color: ${theme.colors.calendar.faint};
    background: transparent;
    border-style: dashed;
    .sw { background: transparent; border: 1.5px solid ${theme.colors.calendar.chipOff}; box-shadow: none; }
    .lbl { text-decoration: line-through; text-decoration-color: ${theme.colors.calendar.chipOff}; }
    .cnt { color: ${theme.colors.calendar.chipOff}; }
  `}

  @media (max-width: 600px) {
    height: 30px;
    font-size: 12.5px;
    padding: 0 10px 0 8px;
    gap: 6px;
    .long, .cnt { display: none; }
    .short { display: inline; }
  }
`;
